import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { type InterviewWithDetails } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";

import {
  type InterviewExperienceResult,
  WORKER_OUTCOME_RESULT,
  type WorkerOutcomeResult,
} from "../../api/useUpdateInterview";
import { FEEDBACK_STEP, type FeedbackStep } from "./feedbackStep";
import { FreeTextStepContent } from "./FreeTextStepContent";
import { InterviewAttendanceConfirmationContent } from "./InterviewAttendanceConfirmationContent";
import { InterviewExperienceStepContent } from "./InterviewExperienceStepContent";
import { ListWorkplaceInterviewFeedbackContent } from "./ListWorkplaceInterviewFeedbackContent";
import { CONFIRMATION_CONFIGS, type FreeTextContext, OPTION_STEP_CONFIGS } from "./optionConstants";
import { OptionSelectionStepContent } from "./OptionSelectionStepContent";
import { ReferralPromptContent } from "./ReferralPromptContent";
import { WorkplaceInterviewFeedbackContent } from "./WorkplaceInterviewFeedbackContent";

interface InterviewAttendanceFeedbackStepContentProps {
  feedbackStep: FeedbackStep | undefined;
  activeInterview: InterviewWithDetails | undefined;
  interviews: InterviewWithDetails[] | undefined;
  placementCandidate: PlacementCandidate;
  freeTextContext: FreeTextContext | undefined;
  hasPendingInterview: boolean | undefined;
  isFetchingInterviews: boolean;
  isGetInterviewsSuccess: boolean;
  isFetchingInterviewsNextPage: boolean;
  hasNextPageInterview: boolean | undefined;
  refetchInterviews: () => Promise<unknown>;
  fetchNextPageInterviews: () => Promise<unknown>;
  onCtaClicked: (toJobsPath?: boolean) => void;
  onFreeTextNeeded: (
    parentStep: FeedbackStep,
    confirmationStep: FeedbackStep,
    result: WorkerOutcomeResult,
    textFieldConfig: { heading: string; placeholder?: string }
  ) => void;
  onFeedbackStepChange: (step: FeedbackStep | undefined) => void;
  onFreeTextContextClear: () => void;
  onInterviewSelected: (interview: InterviewWithDetails) => void;
  onAllSetDismiss: () => void;
  selectedInterviewExperience?: InterviewExperienceResult;
  onInterviewExperienceSelected: (
    experience: InterviewExperienceResult,
    nextStep: FeedbackStep
  ) => void;
}

export function InterviewAttendanceFeedbackStepContent(
  props: InterviewAttendanceFeedbackStepContentProps
) {
  const {
    feedbackStep,
    activeInterview,
    interviews,
    placementCandidate,
    freeTextContext,
    hasPendingInterview,
    isFetchingInterviews,
    isGetInterviewsSuccess,
    isFetchingInterviewsNextPage,
    hasNextPageInterview,
    refetchInterviews,
    fetchNextPageInterviews,
    onCtaClicked,
    onFreeTextNeeded,
    onFeedbackStepChange,
    onFreeTextContextClear,
    onInterviewSelected,
    onAllSetDismiss,
    selectedInterviewExperience,
    onInterviewExperienceSelected,
  } = props;

  const offerOutcomeReferralConfig = useCbhFlag(CbhFeatureFlag.OFFER_OUTCOME_REFERRAL_AMOUNT, {
    defaultValue: { enabled: false, amount: 20 },
  });

  // Render interview experience step when explicitly navigated to OR
  // when auto-entering for a single pending interview
  if (
    activeInterview &&
    (feedbackStep === FEEDBACK_STEP.interviewExperience ||
      (!feedbackStep && interviews?.length === 1 && !selectedInterviewExperience))
  ) {
    return (
      <InterviewExperienceStepContent
        interview={activeInterview}
        placementCandidate={placementCandidate}
        onExperienceSelected={onInterviewExperienceSelected}
      />
    );
  }

  if (feedbackStep) {
    if (feedbackStep === FEEDBACK_STEP.gotOfferWithReferral) {
      return (
        <ReferralPromptContent
          step={feedbackStep}
          placementCandidate={placementCandidate}
          interview={activeInterview}
        />
      );
    }

    const confirmationConfig = CONFIRMATION_CONFIGS[feedbackStep];
    if (confirmationConfig) {
      let ctaText = "Done";
      if (hasPendingInterview) {
        ctaText = "Share next outcome";
      } else if (confirmationConfig.toJobsPath) {
        ctaText = "Keep browsing jobs";
      }

      return (
        <InterviewAttendanceConfirmationContent
          step={feedbackStep}
          placementCandidate={placementCandidate}
          interview={activeInterview}
          title={confirmationConfig.title}
          description={confirmationConfig.description}
          illustration={confirmationConfig.illustration}
          ctaText={ctaText}
          ctaOnClick={() => {
            onCtaClicked(confirmationConfig.toJobsPath);
          }}
        />
      );
    }

    const optionConfig = OPTION_STEP_CONFIGS[feedbackStep];
    if (optionConfig && activeInterview) {
      return (
        <OptionSelectionStepContent
          options={optionConfig.options}
          heading={optionConfig.heading}
          subtitle={optionConfig.subtitle}
          interview={activeInterview}
          step={feedbackStep}
          placementCandidate={placementCandidate}
          refetchInterviews={refetchInterviews}
          isFetchingInterviews={isFetchingInterviews}
          selectedInterviewExperience={selectedInterviewExperience}
          onSubmit={(result: WorkerOutcomeResult) => {
            if (
              feedbackStep === FEEDBACK_STEP.gotOfferSubOptions &&
              result === WORKER_OUTCOME_RESULT.gotOfferKnowNextSteps &&
              offerOutcomeReferralConfig.enabled
            ) {
              onFeedbackStepChange(FEEDBACK_STEP.gotOfferWithReferral);
              return;
            }

            onFeedbackStepChange(optionConfig.nextStep);
          }}
          onFreeTextNeeded={
            optionConfig.freeTextConfirmationStep
              ? (result, textFieldHeading, textFieldPlaceholder) => {
                  onFreeTextNeeded(feedbackStep, optionConfig.freeTextConfirmationStep!, result, {
                    heading: textFieldHeading,
                    placeholder: textFieldPlaceholder,
                  });
                }
              : undefined
          }
        />
      );
    }

    if (feedbackStep === FEEDBACK_STEP.freeText && freeTextContext && activeInterview) {
      return (
        <FreeTextStepContent
          interview={activeInterview}
          step={feedbackStep}
          placementCandidate={placementCandidate}
          result={freeTextContext.result}
          selectedInterviewExperience={selectedInterviewExperience}
          textFieldHeading={freeTextContext.textFieldHeading}
          textFieldPlaceholder={freeTextContext.textFieldPlaceholder}
          refetchInterviews={refetchInterviews}
          isFetchingInterviews={isFetchingInterviews}
          onSubmit={() => {
            onFeedbackStepChange(freeTextContext.confirmationStep);
            onFreeTextContextClear();
          }}
        />
      );
    }
  }

  if (interviews?.length === 0 && isGetInterviewsSuccess) {
    return (
      <InterviewAttendanceConfirmationContent
        placementCandidate={placementCandidate}
        title="You're all set for now"
        description="We'll prompt you here after your next interview so you can confirm the result."
        ctaText="Keep browsing jobs"
        ctaOnClick={onAllSetDismiss}
      />
    );
  }

  if (!activeInterview || !interviews) {
    return (
      <ListWorkplaceInterviewFeedbackContent
        interviews={interviews ?? []}
        isFetchingNextPageInterviews={isFetchingInterviewsNextPage}
        placementCandidate={placementCandidate}
        fetchNextPageInterviews={fetchNextPageInterviews}
        hasNextPageInterview={hasNextPageInterview}
        onInterviewSelected={onInterviewSelected}
      />
    );
  }

  return (
    <WorkplaceInterviewFeedbackContent
      interview={activeInterview}
      placementCandidate={placementCandidate}
      isFetchingInterviews={isFetchingInterviews}
      onOptionSelected={(step) => {
        onFeedbackStepChange(step);
      }}
    />
  );
}
